import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { makeApiRequests } from '../../helpers/api';
import PersonalCalendarButtons from './PersonalCalendarButtons';
import QuickAddModal from '../top-producer/QuickAddModal';
import './store-calendar.css';
const ROOT = '/store-calendar';
const defaultBooking = day => ({ startLocal: day + 'T10:00', endLocal: day + 'T11:00', status: 'Scheduled' });

export default function BookingDialog({ booking, store, statuses, onClose, onSaved, onChangeStore }) {
  const [quickAdd, setQuickAdd] = useState(false);
  const [fields, setFields] = useState(booking.event || defaultBooking(booking.day));
  const [contact, setContact] = useState(booking.contact || null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchError, setSearchError] = useState('');
  const requestId = useRef(uuid());
  const active = useRef(true);
  const searchSequence = useRef(0);
  useEffect(() => () => { active.current = false; }, []);
  const editing = !!booking.event;
  const canEdit = !editing || booking.event.canEdit;
  function change(field, value) { setFields(old => ({ ...old, [field]: value })); }
  async function search() {
    const sequence = ++searchSequence.current;
    setSearching(true); setSearchError(''); setSearched(false); setResults([]);
    const result = await makeApiRequests({ endpoint: `${ROOT}/contacts?store=${encodeURIComponent(store.id)}&q=${encodeURIComponent(query.trim())}`, method: 'GET' });
    if (!active.current || sequence !== searchSequence.current) return;
    setSearching(false);
    if (result.error) setSearchError(result.error);
    else { setResults(result.response.contacts || []); setSearched(true); }
  }
  async function save(event) {
    event.preventDefault();
    if (!canEdit || saving) return;
    if (!editing && !contact) { setError('Choose the customer for this appointment.'); return; }
    setSaving(true); setError('');
    const result = await makeApiRequests({
      endpoint: editing ? `${ROOT}/events/${booking.event.id}` : `${ROOT}/events`,
      method: editing ? 'PUT' : 'POST',
      requestBody: {
        startLocal: fields.startLocal, endLocal: fields.endLocal, status: fields.status,
        ...(editing ? { version: booking.event.version }
          : { storeId: store.id, contactId: contact.id, requestId: requestId.current })
      }
    });
    if (!active.current) return;
    setSaving(false);
    if (result.error || !result.response || !result.response.event || !result.response.event.id) {
      setError(result.error || 'The server did not confirm this booking. Refresh the calendar before creating another appointment.');
      return;
    }
    onSaved(result.response.event);
  }
  const customerName = editing ? booking.event.customer : contact && contact.name;
  const contactId = editing ? booking.event.contactId : contact && contact.id;
  return (<>
    <Modal show onHide={() => !saving && onClose()} backdrop={saving ? 'static' : true} keyboard={!saving} centered>
      <Form onSubmit={save}>
        <Modal.Header closeButton={!saving}><Modal.Title>{editing ? 'Appointment' : 'Book Appointment'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p><strong>{store.name}</strong><br /><span className="text-muted">All times are Central Time.</span></p>
          {onChangeStore && <Button type="button" variant="link" size="sm" disabled={saving} onClick={onChangeStore}>Change sales location</Button>}
          {error && <Alert variant="danger" role="alert">{error}</Alert>}
          {customerName ? (
            <div className="store-calendar-customer">
              <strong>{customerName}</strong>
              <div>{editing ? booking.event.salesperson : contact.salesperson}</div>
              {contactId && <Link to={`/crm/${contactId}`}>Open customer record</Link>}
              {!editing && !saving && <Button variant="link" size="sm" onClick={() => { setContact(null); setResults([]); setSearched(false); }}>Change customer</Button>}
            </div>
          ) : (
            <div className="mb-3">
              <Form.Group controlId="calendar-customer-search">
                <Form.Label>Find a customer</Form.Label>
                <div className="d-flex">
                  <Form.Control value={query} placeholder="Customer name" maxLength={100} disabled={saving}
                    onChange={e => { searchSequence.current += 1; setQuery(e.target.value); setResults([]); setSearched(false); setSearching(false); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (query.trim().length >= 2) search(); } }} />
                  <Button type="button" className="ml-2" onClick={search} disabled={searching || saving || query.trim().length < 2}>Search</Button>
                </div>
              </Form.Group>
              {searchError && <Alert variant="danger">{searchError}</Alert>}
              {searching && <p role="status">Finding customers…</p>}
              {searched && !results.length && <p>No matching customers available to your account in this store.</p>}
              <Button type="button" variant="outline-primary" size="sm" disabled={saving} onClick={() => setQuickAdd(true)}>Create a new customer</Button>
              {results.length > 0 && <ul className="store-calendar-results">{results.map(c => <li key={c.id}><button type="button" onClick={() => { setContact(c); setError(''); }}>{c.name}<span>{c.salesperson}</span></button></li>)}</ul>}
            </div>
          )}
          <fieldset disabled={!canEdit || saving}>
            <Form.Group controlId="calendar-start"><Form.Label>Starts — Central Time</Form.Label>
              <Form.Control type="datetime-local" required min="2000-01-01T00:00" max="2099-12-31T23:59" value={fields.startLocal} onChange={e => change('startLocal', e.target.value)} />
            </Form.Group>
            <Form.Group controlId="calendar-end"><Form.Label>Ends — Central Time</Form.Label>
              <Form.Control type="datetime-local" required min="2000-01-01T00:00" max="2099-12-31T23:59" value={fields.endLocal} onChange={e => change('endLocal', e.target.value)} />
            </Form.Group>
            <Form.Group controlId="calendar-status"><Form.Label>Status</Form.Label>
              <Form.Control as="select" value={fields.status} onChange={e => change('status', e.target.value)}>{statuses.map(s => <option key={s}>{s}</option>)}</Form.Control>
              <Form.Text>To cancel a visit, choose Cancelled and save.</Form.Text>
            </Form.Group>
          </fieldset>
          {!canEdit && <p className="text-muted">The booking salesperson or a store manager can reschedule or cancel this visit.</p>}
          {editing && <PersonalCalendarButtons event={booking.event} store={store}
            dirty={saving || ['startLocal', 'endLocal', 'status'].some(key => fields[key] !== booking.event[key])} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
          {canEdit && <Button type="submit" disabled={saving || (!editing && !contact)}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Book Appointment'}</Button>}
        </Modal.Footer>
      </Form>
    </Modal>
    {quickAdd && <QuickAddModal compact initialBuyer={query} salesLocation={store.id} storeName={store.name}
      onClose={() => setQuickAdd(false)} onCreated={created => {
        setContact({ id: created._id, name: created.buyer, storeId: store.id, salesperson: 'Assigned to you' });
        setResults([]); setSearched(false);
      }} onExisting={async match => {
        const result = await makeApiRequests({ endpoint: `${ROOT}/contacts/${encodeURIComponent(match._id)}`, method: 'GET' });
        if (!active.current) return;
        if (result.error || !result.response?.contact || result.response.contact.storeId !== store.id) {
          setSearchError(result.error || 'This customer belongs to another store.'); setQuickAdd(false); return;
        }
        setContact(result.response.contact); setQuickAdd(false);
      }} />}
    </>
  );
}

